import React, { FC, useEffect, useState, useCallback } from 'react';
import { Theme, useTheme } from '@material-ui/core';
import { Icons, SvgIcon } from '../SvgIcon';
import { Tooltip } from '../Tooltip';
import { SwitchButton } from './SwitchButton';
import { styled } from '@material-ui/core';

type LabeledSwitchButtonProps = {
  label: string;
  icon: Icons;
  tooltip?: React.ReactNode | undefined;
  onChange?: (newValue: boolean) => void;
  checked: boolean;
  fontSize?: number;
};

const IconButtonBase = styled('div')<Theme>(({ theme }) => ({
  position: 'relative',
  width: '2.4rem',
  height: '2.4rem',
  background: theme.palette.common.white,
  boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.15)',
  borderRadius: '100%',
  '&  svg': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const Label = styled('label')<Theme, { fontsize: number }>(({ fontsize }) => ({
  fontSize: `${fontsize}rem`,
  fontWeight: 400,
  cursor: 'pointer',
}));

export const LabeledSwitchButton: FC<LabeledSwitchButtonProps> = ({
  label,
  icon,
  tooltip,
  onChange,
  checked,
  fontSize = 1.3,
}) => {
  const theme = useTheme();
  const [checkedState, setCheckedState] = useState(checked);

  useEffect(() => setCheckedState(checked), [checked]);

  const toggleSwitch = useCallback(() => {
    const newValue = !checkedState;
    setCheckedState(newValue);
    onChange && onChange(newValue);
  }, [checkedState, onChange]);

  const iconComponent = (
    <IconButtonBase>
      <SvgIcon
        icon={icon}
        color={checkedState ? theme.palette.primary.main : theme.palette.grey['500']}
        small
      />
    </IconButtonBase>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="left">
      <div>
        <Label fontsize={fontSize} onClick={toggleSwitch}>
          {label}
        </Label>
        <SwitchButton
          icon={iconComponent}
          checkedIcon={iconComponent}
          checked={checkedState}
          onChange={toggleSwitch}
          color="primary"
        />
      </div>
    </Tooltip>
  ) : (
    <div>
      <Label fontsize={fontSize} onClick={toggleSwitch}>
        {label}
      </Label>
      <SwitchButton
        icon={iconComponent}
        checkedIcon={iconComponent}
        checked={checkedState}
        onChange={toggleSwitch}
        color="primary"
      />
    </div>
  );
};
